import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    Stack,
    Typography,
    Tooltip,
    Button,
    Paper,
    Modal,
    IconButton,
    TextField,
    Snackbar,
    MenuItem
} from '@mui/material';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import WarehouseRoundedIcon from '@mui/icons-material/WarehouseRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import CommentRoundedIcon from '@mui/icons-material/CommentRounded';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import EuroRoundedIcon from '@mui/icons-material/EuroRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RouteRoundedIcon from '@mui/icons-material/RouteRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import useWebSocket from 'react-use-websocket';
import { useCookies } from 'react-cookie';
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineOppositeContent, TimelineSeparator } from '@mui/lab';
import { East, Input, LocalShipping, LocalShippingOutlined, Logout, QrCodeScanner } from '@mui/icons-material';

const socketUrl = 'wss://brmm0q8jaa.execute-api.eu-central-1.amazonaws.com/production';

const statusMap = {
    0: { label: 'Neu', color: 'default', icon: <SyncRoundedIcon fontSize="small" /> },
    1: { label: 'In Bearbeitung', color: 'info', icon: <SyncRoundedIcon fontSize="small" /> },
    2: { label: 'Abholbereit', color: 'warning', icon: <Inventory2RoundedIcon fontSize="small" /> },
    3: { label: 'Abgeholt', color: 'success', icon: <CheckCircleRoundedIcon fontSize="small" /> },
    4: { label: 'Retoure', color: 'secondary', icon: <SyncRoundedIcon fontSize="small" /> },
    5: { label: 'Fehler', color: 'error', icon: <ErrorOutlineRoundedIcon fontSize="small" /> }
};

const paymentStatusMap = {
    succeeded: { label: 'Bezahlt', color: 'success' },
    processing: { label: 'Zahlung läuft', color: 'warning' },
    requires_payment_method: { label: 'Zahlung fehlgeschlagen', color: 'error' },
    canceled: { label: 'Zahlung abgebrochen', color: 'default' }
};

function formatDate(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
        return new Intl.DateTimeFormat('de-DE', {
            dateStyle: 'medium',
            timeStyle: typeof value === 'string' && value.includes(':') ? 'short' : undefined
        }).format(date);
    }
    return value;
}

function formatDateOnly(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
        return new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' }).format(date);
    }
    return value;
}

function formatCurrency(details) {
    if (details?.priceCents != null && !Number.isNaN(Number(details.priceCents))) {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR'
        }).format(Number(details.priceCents) / 100);
    }

    if (details?.preis) {
        return `${details.preis} €`;
    }

    return '—';
}

function yesNo(value) {
    if (value == null) return '—';
    return Number(value) === 1 ? 'Ja' : 'Nein';
}

function getStatusMeta(status) {
    return statusMap[status] || {
        label: `Status ${status ?? '—'}`,
        color: 'default',
        icon: <SyncRoundedIcon fontSize="small" />
    };
}

function getPaymentMeta(details) {
    if (Number(details?.onlinePayed) === 1 && !details?.paymentProcessingStatus) {
        return { label: 'Online bezahlt', color: 'success' };
    }

    if (details?.paymentProcessingStatus && paymentStatusMap[details.paymentProcessingStatus]) {
        return paymentStatusMap[details.paymentProcessingStatus];
    }

    if (details?.paymentProcessingStatus) {
        return { label: details.paymentProcessingStatus, color: 'default' };
    }

    if (Number(details?.onlinePayed) === 1) {
        return { label: 'Online bezahlt', color: 'success' };
    }

    return { label: 'Keine Online-Zahlung', color: 'default' };
}

function CompactInfoRow({ icon, label, value, mono = false, emphasize = false, children }) {
    return (
        <Stack
            direction="row"
            spacing={1.1}
            alignItems="flex-start"
            sx={{ py: 0.8, minWidth: 0 }}
        >
            <Avatar
                variant="rounded"
                sx={{
                    width: 26,
                    height: 26,
                    bgcolor: 'action.hover',
                    color: 'text.secondary'
                }}
            >
                {icon}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                    {label}
                </Typography>
                {children || (
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: emphasize ? 800 : 600,
                            lineHeight: 1.35,
                            fontFamily: mono ? 'monospace' : 'inherit',
                            wordBreak: 'break-word'
                        }}
                    >
                        {value || '—'}
                    </Typography>
                )}
            </Box>
        </Stack>
    );
}

function DenseKpiTile({ icon, label, value, caption, accent = 'default' }) {
    const accentMap = {
        default: {
            borderColor: 'divider',
            background: 'linear-gradient(180deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0) 100%)'
        },
        success: {
            borderColor: 'success.light',
            background: 'linear-gradient(180deg, rgba(46,125,50,0.10) 0%, rgba(46,125,50,0.02) 100%)'
        },
        warning: {
            borderColor: 'warning.light',
            background: 'linear-gradient(180deg, rgba(237,108,2,0.10) 0%, rgba(237,108,2,0.02) 100%)'
        },
        info: {
            borderColor: 'info.light',
            background: 'linear-gradient(180deg, rgba(2,136,209,0.10) 0%, rgba(2,136,209,0.02) 100%)'
        }
    };

    return (
        <Box
            sx={{
                p: 1.4,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: accentMap[accent].borderColor,
                background: accentMap[accent].background,
                minWidth: 0,
            }}
        >
            <Stack direction="row" spacing={1} alignItems="center">
                <Avatar
                    variant="rounded"
                    sx={{
                        width: 30,
                        height: 30,
                        bgcolor: 'background.paper',
                        color: 'text.secondary'
                    }}
                >
                    {icon}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.05 }}>
                        {label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                        {value || '—'}
                    </Typography>
                    {caption ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.15 }}>
                            {caption}
                        </Typography>
                    ) : null}
                </Box>
            </Stack>
        </Box>
    );
}

function SectionCard({ title, icon, children, action }) {
    return (
        <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Box
                sx={{
                    px: 1.75,
                    py: 1.1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    background: 'linear-gradient(180deg, rgba(0,0,0,0.025) 0%, rgba(0,0,0,0) 100%)'
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar
                            variant="rounded"
                            sx={{
                                width: 28,
                                height: 28,
                                bgcolor: 'action.hover',
                                color: 'text.secondary'
                            }}
                        >
                            {icon}
                        </Avatar>
                        <Typography variant="body1" sx={{ fontWeight: 900 }}>
                            {title}
                        </Typography>
                    </Stack>
                    {action}
                </Stack>
            </Box>
            <Box sx={{ p: 1.75 }}>{children}</Box>
        </Card>
    );
}


function EditableField({ label, value, onChange, multiline = false, rows = 1, select = false, children }) {
    return (
        <TextField
            fullWidth
            size="small"

            value={value ?? ''}
            onChange={onChange}
            multiline={multiline}
            rows={rows}
            select={select}
        >
            {children}
        </TextField>
    );
}

export default function ShipmentDetailsView({ sid, onModify = () => { } }) {
    const [details, setDetails] = useState(null);
    const [customer, setCustomer] = useState(null);
    const [history, setHistory] = useState([]);
    const [prices, setPrices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [cookies] = useCookies(['secret', 'station', 'staff']);
    const [editOpen, setEditOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState('');

    const [rqidGet, setRqidGet] = useState();
    const [rqidUpd, setRqidUpd] = useState();

    const [editForm, setEditForm] = useState({
        klasse: '',
        absender: '',
        sendungsnummer: '',
        comment: '',
        spedition: ''
    });

    const { sendJsonMessage } = useWebSocket(socketUrl, {
        queryParams: {
            station: cookies.station,
            secret: cookies.secret,
            staff: cookies.staff
        },
        shouldReconnect: (_closeEvent) => true,
        retryOnError: true,
        onMessage: (message) => handleWebsocketEvent(message),
    });

    useEffect(() => {
        if (!sid) return;

        setLoading(true);
        setError('');
        const newRqId = Math.floor(Math.random() * 100000000);
        setRqidGet(newRqId)
        sendJsonMessage({ task: 'getShipmentDetailsAndHistory', station: cookies.station, staff: cookies.staff, sid, rqid: newRqId });
    }, [sid, sendJsonMessage]);

    useEffect(() => {
        if (!details) return;
        setEditForm({
            klasse: details.klasse || '',
            absender: details.absender || '',
            sendungsnummer: details.sendungsnummer || '',
            comment: details.comment || '',
            spedition: details.spedition || ''
        });
    }, [details]);

    const handleWebsocketEvent = (lastMessage) => {
        const msg = JSON.parse(lastMessage.data);

        if (!msg) return;

        if (msg?.task === 'respGetShipmentDetailsAndHistory' && msg.rqid === rqidGet) {
            setDetails(msg.details || null);
            setCustomer(msg.customer || null);
            setHistory(Array.isArray(msg.history) ? msg.history : []);
            setPrices(Array.isArray(msg.prices) ? msg.prices : []);
            setLoading(false);
        }

        if (msg?.task === 'respUpdateShipmentData' && msg.rqid === rqidUpd) {
            setSaving(false);
            if (msg?.status === 'SUCCEEDED') {
                setSnackbar('Sendungsdaten gespeichert');
                setEditOpen(false);
                onModify();
                const newRqId = Math.floor(Math.random() * 100000000);
                setRqidGet(newRqId)
                sendJsonMessage({ task: 'getShipmentDetailsAndHistory', station: cookies.station, staff: cookies.staff, sid, rqid: newRqId });
            } else {
                setSnackbar('Speichern fehlgeschlagen');
            }
        }
    };

    const shipmentStatus = useMemo(() => getStatusMeta(details?.status), [details]);
    const paymentMeta = useMemo(() => getPaymentMeta(details || {}), [details]);
    const isPickedUp = Number(details?.status) === 3;
    const canEdit = !isPickedUp;

    const selectedPrice = useMemo(
        () => prices.find((p) => p.Klasse === editForm.klasse),
        [prices, editForm.klasse]
    );

    const openEdit = () => {
        if (!details || !canEdit) return;
        setEditForm({
            klasse: details.klasse || '',
            absender: details.absender || '',
            sendungsnummer: details.sendungsnummer || '',
            comment: details.comment || '',
            spedition: details.spedition || ''
        });
        setEditOpen(true);
    };

    const cancelEdit = () => {
        setEditForm({
            klasse: details?.klasse || '',
            absender: details?.absender || '',
            sendungsnummer: details?.sendungsnummer || '',
            comment: details?.comment || '',
            spedition: details?.spedition || ''
        });
        setEditOpen(false);
    };

    const saveEdit = () => {
        setSaving(true);
        const newRqId = Math.floor(Math.random() * 100000000);
        setRqidUpd(newRqId)
        sendJsonMessage({
            task: 'updateShipmentData',
            station: cookies.station,
            staff: cookies.staff,
            sid,
            rqid: newRqId,
            ...editForm
        });
    };

    if (loading) {
        return (
            <Box
                sx={{
                    minHeight: 420,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Stack spacing={2} alignItems="center">
                    <CircularProgress size={28} />
                    <Typography color="text.secondary">
                        Sendungsdetails werden geladen …
                    </Typography>
                </Stack>
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (!details) {
        return <Alert severity="warning">Keine Sendungsdetails gefunden.</Alert>;
    }

    return (
        <>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    gap: 2,
                    minHeight: '100%'
                }}
            >
                <Card
                    variant="outlined"
                    sx={{
                        borderRadius: 4,
                        overflow: 'hidden',
                        borderColor: isPickedUp ? 'success.light' : 'warning.light',
                        background: isPickedUp
                            ? 'linear-gradient(135deg, rgba(46,125,50,0.10) 0%, rgba(46,125,50,0.02) 45%, rgba(0,0,0,0) 100%)'
                            : 'linear-gradient(135deg, rgba(237,108,2,0.10) 0%, rgba(237,108,2,0.02) 45%, rgba(0,0,0,0) 100%)'
                    }}
                >
                    <CardContent sx={{ p: 2 }}>
                        <Stack spacing={1.5}>
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                justifyContent="space-between"
                                spacing={1.5}
                            >
                                <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
                                    <Avatar
                                        variant="rounded"
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            bgcolor: isPickedUp ? 'success.main' : 'warning.main',
                                            color: 'common.white'
                                        }}
                                    >
                                        {isPickedUp ? <CheckCircleRoundedIcon /> : <WarehouseRoundedIcon />}
                                    </Avatar>

                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>
                                            {isPickedUp ? 'Abgeholte Sendung' : 'Lagernde Sendung'}
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            sx={{ fontWeight: 900, lineHeight: 1.15, wordBreak: 'break-word' }}
                                        >
                                            {details.id}
                                        </Typography>
                                    </Box>
                                   
                                </Stack>

                                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                                    <Tooltip title="Mail wurde versendet?">
                                        <Chip size="small" label={`Mail: ${yesNo(details.mail_sent)}`} variant="outlined" />
                                    </Tooltip>
                                    {canEdit && !editOpen && (
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<EditRoundedIcon />}
                                            onClick={openEdit}
                                        >
                                            Bearbeiten
                                        </Button>
                                    )}
                                    {canEdit && editOpen && (
                                        <>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<CloseRoundedIcon />}
                                                onClick={cancelEdit}
                                                disabled={saving}
                                            >
                                                Abbrechen
                                            </Button>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<SaveRoundedIcon />}
                                                onClick={saveEdit}
                                                disabled={saving}
                                            >
                                                {saving ? 'Speichern …' : 'Speichern'}
                                            </Button>
                                        </>
                                    )}
                                </Stack>
                            </Stack>

                            <Grid container spacing={1.25}>
                                {isPickedUp ? (
                                    <>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <DenseKpiTile
                                                icon={<CalendarMonthRoundedIcon fontSize="small" />}
                                                label="Abholdatum"
                                                value={formatDateOnly(details.abholdatum2Analytics || details.abholdatum2)}
                                                caption="maßgeblich für Abholung"
                                                accent="success"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <DenseKpiTile
                                                icon={<PaymentsRoundedIcon fontSize="small" />}
                                                label="Zahlungsstatus"
                                                value={paymentMeta.label}
                                                caption={`Online bezahlt: ${yesNo(details.onlinePayed)}`}
                                                accent="success"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <DenseKpiTile
                                                icon={<ReceiptLongRoundedIcon fontSize="small" />}
                                                label="Rechnungsstatus"
                                                value={details.rechnstatus ?? '—'}
                                                caption="abgeholte Sendung"
                                                accent="success"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <DenseKpiTile
                                            icon={<PersonRoundedIcon fontSize="small" />}
                                            label="Kunde"
                                            value={details.kdnrString || details.kdnr}
                                            caption={customer.firstname + " " + customer.lastname}
                                            accent="default"
                                        />
                                        </Grid>
                                    </>
                                ) : (
                                    <>
<DenseKpiTile
                                            icon={<PersonRoundedIcon fontSize="small" />}
                                            label="Kunde"
                                            value={details.kdnrString || details.kdnr}
                                            caption={customer.firstname + " " + customer.lastname}
                                            accent="default"
                                        />

                                    </>
                                )}
                            </Grid>
                        </Stack>
                    </CardContent>
                </Card>

                <Grid container spacing={2}>
                    <Grid item xs={12} lg={7}>
                        {!isPickedUp ? (
                            <SectionCard
                                title="Lagerdetails"
                                icon={<WarehouseRoundedIcon />}
                            >
                                <Grid container spacing={1.5}>
                                    <Grid item xs={12} md={6}>
                                        <CompactInfoRow icon={<PersonRoundedIcon fontSize="small" />} label="Absender" value={details.absender} emphasize>
                                            {editOpen ? (
                                                <EditableField
                                                    label="Absender"
                                                    value={editForm.absender}
                                                    onChange={(e) => setEditForm((s) => ({ ...s, absender: e.target.value }))}
                                                />
                                            ) : null}
                                        </CompactInfoRow>
                                        <CompactInfoRow icon={<RouteRoundedIcon fontSize="small" />} label="Spedition" value={details.spedition}>
                                            {editOpen ? (
                                                <EditableField
                                                    label="Spedition"
                                                    value={editForm.spedition}
                                                    onChange={(e) => setEditForm((s) => ({ ...s, spedition: e.target.value }))}
                                                />
                                            ) : null}
                                        </CompactInfoRow>
                                        <CompactInfoRow icon={<BadgeRoundedIcon fontSize="small" />} label="Erstellt von" value={details.creator} />
                                        <CompactInfoRow icon={<WarehouseRoundedIcon fontSize="small" />} label="Lager" value={details.lagerName} emphasize />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <CompactInfoRow icon={<CalendarMonthRoundedIcon fontSize="small" />} label="Erstellt" value={formatDate(details.creationdateAnalytics || details.creationdate)} />
                                        <CompactInfoRow icon={<CalendarMonthRoundedIcon fontSize="small" />} label="Lieferdatum" value={formatDate(details.deliverydate)} />
                                        <CompactInfoRow icon={<AccessTimeRoundedIcon fontSize="small" />} label="Abholen bis" value={formatDateOnly(details.abholen_bisAnalytics || details.abholen_bis)} emphasize />
                                        <CompactInfoRow icon={<AssignmentTurnedInRoundedIcon fontSize="small" />} label="Klasse" value={details.klasse}>
                                            {editOpen ? (
                                                <EditableField
                                                    label="Klasse"
                                                    value={editForm.klasse}
                                                    onChange={(e) => setEditForm((s) => ({ ...s, klasse: e.target.value }))}
                                                    select
                                                >
                                                    {prices.map((price) => (
                                                        <MenuItem key={price.ID} value={price.Klasse}>
                                                            {price.Klasse} - {price.text} - {new Intl.NumberFormat('de-DE', {
                                                                style: 'currency',
                                                                currency: 'EUR'
                                                            }).format(Number(price.price) / 100)}
                                                        </MenuItem>
                                                    ))}
                                                </EditableField>
                                            ) : null}
                                        </CompactInfoRow>
                                    </Grid>
                                </Grid>
                            </SectionCard>
                        ) : (
                            <SectionCard
                                title="Abholdaten"
                                icon={<CheckCircleRoundedIcon />}
                                action={<Chip size="small" label="Nicht bearbeitbar" color="success" variant="outlined" />}
                            >
                                <Grid container spacing={1.5}>
                                    <Grid item xs={12} md={6}>
                                        <CompactInfoRow
                                            icon={<CalendarMonthRoundedIcon fontSize="small" />}
                                            label="Abholdatum"
                                            value={formatDateOnly(details.abholdatum2Analytics || details.abholdatum2)}
                                            emphasize
                                        />
                                        <CompactInfoRow
                                            icon={<PaymentsRoundedIcon fontSize="small" />}
                                            label="Zahlungsstatus"
                                            value={paymentMeta.label}
                                            emphasize
                                        />
                                        <CompactInfoRow
                                            icon={<PaymentsRoundedIcon fontSize="small" />}
                                            label="Online bezahlt"
                                            value={yesNo(details.onlinePayed)}
                                        />
                                        <CompactInfoRow
                                            icon={<AssignmentTurnedInRoundedIcon fontSize="small" />}
                                            label="Rechnungsstatus"
                                            value={details.rechnstatus ?? '—'}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <CompactInfoRow
                                            icon={<BadgeRoundedIcon fontSize="small" />}
                                            label="Payment Intent"
                                            value={details.paymentIntentId}
                                            mono
                                        />
                                        <CompactInfoRow
                                            icon={<SyncRoundedIcon fontSize="small" />}
                                            label="Processing Status"
                                            value={details.paymentProcessingStatus}
                                            mono
                                        />
                                        <CompactInfoRow
                                            icon={<BadgeRoundedIcon fontSize="small" />}
                                            label="Abholer-ID"
                                            value={details.abholerID}
                                            mono
                                        />
                                        <CompactInfoRow
                                            icon={<PersonRoundedIcon fontSize="small" />}
                                            label="Absender"
                                            value={details.absender}
                                        />
                                    </Grid>
                                </Grid>
                            </SectionCard>
                        )}
                    </Grid>

                    <Grid item xs={12} lg={5}>
                        {!isPickedUp ? (
                            <SectionCard
                                title="Versand & Identifikation"
                                icon={<LocalShippingRoundedIcon />}
                            >
                                <Stack spacing={0.25}>
                                    <CompactInfoRow icon={<QrCode2RoundedIcon fontSize="small" />} label="Sendungsnummer" value={details.sendungsnummer} mono>
                                        {editOpen ? (
                                            <EditableField
                                                label="Sendungsnummer"
                                                value={editForm.sendungsnummer}
                                                onChange={(e) => setEditForm((s) => ({ ...s, sendungsnummer: e.target.value }))}
                                            />
                                        ) : null}
                                    </CompactInfoRow>
                                    <CompactInfoRow icon={<PersonRoundedIcon fontSize="small" />} label="Absender BDV" value={details.absender_bdv} />
                                    <CompactInfoRow icon={<WarehouseRoundedIcon fontSize="small" />} label="Geliefert von" value={details.deliveredby} />
                                    <CompactInfoRow icon={<BadgeRoundedIcon fontSize="small" />} label="Sign-ID" value={details.signid} mono />
                                </Stack>
                            </SectionCard>
                        ) : (
                            <SectionCard
                                title="Herkunft & Versand"
                                icon={<LocalShippingRoundedIcon />}
                            >
                                <Stack spacing={0.25}>
                                    <CompactInfoRow icon={<RouteRoundedIcon fontSize="small" />} label="Spedition" value={details.spedition} />
                                    <CompactInfoRow icon={<AssignmentTurnedInRoundedIcon fontSize="small" />} label="Klasse" value={details.klasse} />
                                    <CompactInfoRow icon={<CalendarMonthRoundedIcon fontSize="small" />} label="Erstellt" value={formatDate(details.creationdateAnalytics || details.creationdate)} />
                                    <CompactInfoRow icon={<CalendarMonthRoundedIcon fontSize="small" />} label="Lieferdatum" value={formatDate(details.deliverydate)} />
                                    <CompactInfoRow icon={<WarehouseRoundedIcon fontSize="small" />} label="Geliefert von" value={details.deliveredby} />
                                </Stack>
                            </SectionCard>
                        )}
                    </Grid>

{/**
                    <Grid item xs={12}>
                        <SectionCard
                            title="Kommentar & Hinweise"
                            icon={<CommentRoundedIcon />}
                        >
                            <Grid container spacing={1.5}>
                                <Grid item xs={12} md={6}>
                                    <Box
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 2,
                                            bgcolor: 'action.hover',
                                            minHeight: 84
                                        }}
                                    >
                                        <Typography variant="caption" color="text.secondary">
                                            Kommentar
                                        </Typography>
                                        {editOpen && canEdit ? (
                                            <Box sx={{ mt: 1 }}>
                                                <EditableField
                                                    label="Kommentar"
                                                    value={editForm.comment}
                                                    onChange={(e) => setEditForm((s) => ({ ...s, comment: e.target.value }))}
                                                    multiline
                                                    rows={4}
                                                />
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: 'pre-wrap', fontWeight: 500 }}>
                                                {details.comment || 'Kein Kommentar vorhanden.'}
                                            </Typography>
                                        )}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Box
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 2,
                                            bgcolor: 'action.hover',
                                            minHeight: 84
                                        }}
                                    >
                                        <Typography variant="caption" color="text.secondary">
                                            Abholer-Text
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: 'pre-wrap', fontWeight: 500 }}>
                                            {details.abholerText || 'Kein Abholer-Text vorhanden.'}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </SectionCard>
                    </Grid>
*/}
                    <Grid item xs={12}>
                        <SectionCard
                            title="Verlauf"
                            icon={<CommentRoundedIcon />}
                        >
                            <Grid container spacing={1.5}>
                                <Grid item xs={12} md={6}>

                                    <Timeline position="left">
                                        {history.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()).map((e, i) => {
                                            return (
                                                <TimelineItem key={i}>
                                                    <TimelineOppositeContent sx={{ pt: 3 }}>
                                                        {new Date(e.timestamp).toLocaleString('de-DE')}
                                                        <Typography variant="caption" component="p">{e.staffName} {e.did && <>({e.did})</>}</Typography>
                                                        <small>{e.comment}</small>
                                                    </TimelineOppositeContent>
                                                    <TimelineSeparator>
                                                        <TimelineConnector />
                                                        <TimelineDot color="primary">
                                                            {getActionIcon(e.action)}
                                                        </TimelineDot>
                                                        <TimelineConnector />
                                                    </TimelineSeparator>
                                                    <TimelineContent sx={{ py: 2 }}>
                                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                            {getActionName(e.action)}
                                                        </Typography>
                                                        <Typography><Chip label={e.previousLagerName} size="small" /> <East sx={{ fontSize: '16px' }} /> <Chip label={e.lagerName} size="small" /></Typography>
                                                    </TimelineContent>
                                                </TimelineItem>
                                            );
                                        })}
                                    </Timeline>
                                </Grid>
                            </Grid>
                        </SectionCard>
                    </Grid>
                </Grid>
            </Box>

            <Snackbar
                open={!!snackbar}
                autoHideDuration={2800}
                onClose={() => setSnackbar('')}
                message={snackbar}
            />
        </>
    );
}

const ShipmentDetailViewButton = ({ sid, onModify }) => {
    const [open, setOpen] = useState(false);

    return (
        <Box>
            {open && <ShipmentDetailViewModal onClose={() => setOpen(false)} sid={sid} onModify={onModify} />}
            <Button onClick={() => setOpen(true)}>{sid}</Button>
        </Box>
    );
};

export { ShipmentDetailViewButton };

const ShipmentDetailViewModal = ({ onClose, onModify, sid }) => {
    return (
        <Modal open={true}>
            <Paper
                sx={{
                    width: 'min(1200px, calc(100vw - 32px))',
                    maxHeight: 'calc(100vh - 32px)',
                    overflow: 'auto',
                    margin: '16px auto',
                    borderRadius: 3
                }}
            >
                <Box
                    sx={{
                        px: 2,
                        py: 1.25,
                        position: 'sticky',
                        top: 0,
                        zIndex: 5,
                        bgcolor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                            Sendungsdetails
                        </Typography>
                        <Button onClick={onClose}>Close</Button>
                    </Stack>
                </Box>
                <Box sx={{ p: 2 }}>
                    <ShipmentDetailsView sid={sid} onModify={onModify} />
                </Box>
            </Paper>
        </Modal>
    );
};

const getActionName = (key) => {
    return { 'CHECKIN': 'Erfassung', 'RELOCATE': 'Ein-/Umlagerung', 'CHECKOUT': 'Ausgabe', 'INVENTORY': 'Inventur-Scan', 'UPDATE_SHIPMENT_DATA': 'Sendungedaten verändert' }[key];
}

const getActionIcon = (key) => {
    return { 'CHECKIN': <LocalShippingOutlined />, 'RELOCATE': <Input />, 'CHECKOUT': <Logout />, 'INVENTORY': <QrCodeScanner /> }[key];
}